/*! Copyright (c) 2022, XAPP AI */
import { WidgetTheme } from "@xapp/chat-widget";
import { ChatWidgetAppChannelInput, ChatWidgetServerType, CtaAnimation } from "generated/graphql";

import { getApiUrl, getRouterUrl } from "./getApiUrl";
import { invertColor, invertColorHEX } from "./color";

/**
 * Builds a default theme based on a given primary color
 */
export function buildDefaultChatTheme(primaryColor: string, secondaryColor?: string): WidgetTheme {
    const fontFamily = "RobotoRegular";

    if (!secondaryColor) {
        secondaryColor = invertColor(primaryColor, true);
    }

    return {
        zIndex: "10000",
        border: {
            color: primaryColor,
            radius: "10px",
        },
        carousel: {
            button: {
                color: primaryColor,
            },
            subtitle: {
                color: "#979797",
                fontFamily,
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: "normal",
            },
            title: {
                color: "#000",
                fontFamily,
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "bold",
            },
        },
        chatButton: {
            background: primaryColor,
            margin: {
                bottom: "10px",
                left: "10px",
                right: "10px",
                top: "10px",
            },
        },
        content: {
            background: "#ffffff",
        },
        cta: {
            background: "#e1e2e1",
            text: {
                // Use a dark color that contrasts well with the light grey background
                // instead of primaryColor which may not have sufficient contrast
                color: "#333333",
                fontFamily,
                fontSize: "14px",
            },
        },
        footer: {
            background: primaryColor,
            border: {
                color: primaryColor,
                radius: "0px",
                width: "0px",
            },
            branding: {
                text: {
                    color: "#ffffff",
                    fontFamily,
                    fontSize: "14px",
                    fontWeight: "normal",
                    fontStyle: "normal",
                },
            },
        },
        header: {
            background: primaryColor,
            avatarBackground: "transparent",
            border: {
                color: primaryColor,
                radius: "0px",
                width: "0px",
            },
            text: {
                color: secondaryColor,
                fontFamily,
                fontSize: "16px",
                fontWeight: "normal",
                fontStyle: "normal",
            },
            subtitle: {
                color: secondaryColor,
                fontFamily,
                fontSize: "10px",
                fontWeight: "normal",
                fontStyle: "normal",
            },
        },
        input: {
            background: "#ffffff",
            border: {
                radius: "20px",
            },
            text: {
                color: "#000000",
                fontFamily,
                fontSize: "14px",
            },
            placeholder: {
                color: "#e1e2e1",
                fontFamily,
                fontSize: "14px",
                fontWeight: "normal",
                fontStyle: "normal",
            },
        },
        textTypingStatus: {
            text: {
                color: "#bfbfbf",
                fontFamily,
                fontSize: "14px",
                fontWeight: "normal",
                fontStyle: "normal",
            },
        },
        menu: {
            item: {
                height: "24px",
                text: {
                    fontSize: "14px",
                },
            },
        },
        menuButton: {
            color: "#ffffff",
        },
        messages: {
            maxWidth: "400px",
            mine: {
                bubbleColor: "#f2f2f2",
                text: {
                    color: "#454545",
                    fontFamily,
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: "normal",
                },
            },
            others: {
                bubbleColor: "#e1e2e1",
                text: {
                    color: "#262726",
                    fontFamily,
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: "normal",
                },
            },
            padding: {
                bottom: "0px",
                left: "20px",
                right: "16px",
                top: "16px",
            },
        },
        refreshButton: {
            color: secondaryColor,
        },
        minimizeButton: {
            color: secondaryColor,
        },
        cancelButton: {
            color: secondaryColor,
        },
        primaryColor: primaryColor,
        sendButton: {
            color: "#505050",
            width: "20px",
        },
        size: {
            width: "500px",
            height: "700px",
        },
    };
}

export interface ChatWidgetInput {
    name: string;
    website?: string;
    logo?: string;
    primaryColor: string;
    secondaryColor?: string;
    appId: string;
    templateId: string;
}

/**
 * Generates the input for the createAppWidget mutation
 *
 * @param appInput
 * @returns
 */
export function generateCreateAppWidgetInput(
    appInput: ChatWidgetInput,
    includeFooter?: boolean
): ChatWidgetAppChannelInput {
    const { name, logo, appId, templateId } = appInput;

    // Urls - Use template keys for ID and KEY

    const apiUrl = getApiUrl();
    const wssUrl = getRouterUrl();

    const autocompleteSuggestionsUrl = `${apiUrl}/chat/suggestions?key=$CHANNEL_KEY&type=INTENT,FAQ&templateAppId=${templateId}`;
    const serverUrl = `${apiUrl}/apps/${appId}/run/`;
    const routerUrl = `${wssUrl}/?appId=${appId}&channelId=$CHANNEL_ID`;

    let primaryColor = appInput.primaryColor;
    let buttonColor: string;

    // if no primary color, use the default
    if (!primaryColor) {
        primaryColor = "#F9F9F9";
        buttonColor = "#333333";
    } else {
        buttonColor = invertColorHEX(primaryColor, true);
    }

    const widgetInput: ChatWidgetAppChannelInput = {
        type: "chat-widget",
        cta: {
            message: "Need Help? Click Here",
            timeout: 30000,
            delay: 2000,
            animation: "bounce" as CtaAnimation,
            animationTimeout: 5000,
            mobile: {
                applyAtLessThanWidth: "480",
                delay: 0,
                animation: "wiggle" as CtaAnimation,
                animationTimeout: 5000,
            },
        },
        serverUrl,
        connection: {
            type: ChatWidgetServerType.Websocketraw,
            serverUrl: routerUrl,
        },
        autocompleteSuggestionsUrl,
        direct: false,

        botName: name,

        header: {
            status: {
                online: name,
            },
            actions: {
                minimize: true,
                cancel: true,
            },
        },
        urls: {
            policies: [],
            defaultBehavior: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Trust us
                type: "newTab",
            },
        },
        theme: buildDefaultChatTheme(primaryColor, buttonColor),
        configurableMessages: {
            items: [
                {
                    delay: 12,
                    text: "Thinking... will have your answer shortly... ",
                },
                {
                    delay: 5,
                    text: "Still thinking... thanks for your patience.",
                },
                {
                    delay: 0,
                    text: "Sorry, I couldn't get the answer for you.  This might be a connection error, in that case please try again later.",
                },
            ],
        },
    };

    if (includeFooter) {
        widgetInput.footer = {
            branding: {
                text: "Powered by XAPP.AI",
            },
        };
    }

    if (appInput.website) {
        const { website } = appInput;

        let url: URL;

        try {
            url = new URL(website);
        } catch (e) {
            throw new Error(`Website ${website} is invalid.`);
        }

        let patternedWebsite = `${url.hostname}${url.pathname}`;

        // Hosts often treat www. and hostnames without subdomains
        // as exactly the same website so we remove the www. to also
        // match same site
        // If domain is www then we remove it
        if (patternedWebsite.toLowerCase().startsWith("www.")) {
            patternedWebsite = patternedWebsite.replace(/www\./i, "");
            // https://[(www.)?]bonedry.com/cincinnati[.+]
        }

        widgetInput.urls.policies.push({
            pattern: patternedWebsite,
            behavior: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Trust us
                type: "sameWindow",
            },
        });
    }

    if (logo) {
        widgetInput.avatarUrl = logo;
    }

    return widgetInput;
}
